import { fetchUtils } from 'react-admin'
import simpleRestProvider from 'ra-data-simple-rest'
import { espaceUniquePrestaPrefix } from './utils'
import config from './config'

const httpClient = (url, options = {}) => {
  //This is a static token protection measure, in addition to everything else.
  //Useful for ext_ endpoints that are not protected by oauth
  const auth_token = config.auth_token
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' })
  }
  const localStorageKeys = Object.keys(window.context)
  localStorageKeys.forEach((key) => {
    if (key.indexOf('context') !== -1) {
      const context = window.context[key]
      options.headers.set(key, `${context}`)
    }
  })
  options.headers.set('locationorigin', window.location.origin)
  options.headers.set('auth_token', auth_token)

  //For frontplugin
  if (window.context['front-teammate-email'] !== undefined) {
    options.headers.set(
      'front-teammate-email',
      window.context['front-teammate-email']
    )
  }
  return fetchUtils.fetchJson(url, options)
}

const dataProvider = simpleRestProvider(config.API_V1, httpClient)

//We use the same key alternatively for base64 ou filepath in back and front.
//If the obj is a file object, we need to convert it to base64
//If the obj is a base64 string, we do nothing
const isFile = (obj) => {
  if (obj === undefined) {
    return false
  }
  if (obj === null) {
    return false
  }
  if (obj.src !== undefined) {
    return true
  }
  return false
}

const image_fields = {
  copros_general_news: [{ key: 'files', resize: true }],
  ext_agregistrations: [{ key: 'id_path', resize: true }], //NOTE: signature component sends base64 directly, no conversion needed
  contracts: [
    { key: 'typegeneral_fichier', resize: false },
    { key: 'typesyndic_fichier', resize: false },
    { key: 'typeassurance_fichier', resize: false },
  ],
  copros_fiche: [
    { key: 'fiche_gardien1_contrat_travail', resize: false },
    { key: 'fiche_gardien1_contrat_visite_medicale', resize: false },
    { key: 'fiche_gardien1_contrat_prevoyance', resize: false },
    { key: 'fiche_gardien1_contrat_mutuelle', resize: false },
    { key: 'fiche_gardien2_contrat_travail', resize: false },
    { key: 'fiche_gardien2_contrat_visite_medicale', resize: false },
    { key: 'fiche_gardien2_contrat_prevoyance', resize: false },
    { key: 'fiche_gardien2_contrat_mutuelle', resize: false },
    { key: 'fiche_reglement_copro', resize: false },
    { key: 'fiche_modificatifs', resize: false },
    { key: 'fiche_edd', resize: false },
    { key: 'fiche_dernier_pvag', resize: false },
    { key: 'fiche_immatriculation', resize: false },
    { key: 'fiche_diagnostic_amiante_document', resize: false },
    { key: 'fiche_diagnostic_plomb_document', resize: false },
    { key: 'fiche_diagnostic_termites_document', resize: false },
  ],
  signrequests: [{ key: 'pv', resize: false }],
  invoices1: [
    { key: 'process5_facture', resize: false },
    { key: 'process5_rib', resize: false },
  ],
}

const convertFileToBase64 = (field) => {
  const file = field.record
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file.rawFile)

    reader.onload = () => {
      //we don't know how to resize pdf
      if (field.resize === false || file.title.indexOf('.pdf') !== -1) {
        resolve({ base64: reader.result, ...field })
      } else {
        //https://zocada.com/compress-resize-images-javascript-browser/
        const img = document.createElement('img')
        img.src = reader.result
        img.onload = () => {
          const width = 800
          const scaleFactor = width / img.width
          const height = img.height * scaleFactor
          const elem = document.createElement('canvas')
          elem.width = width
          elem.height = height
          const ctx = elem.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)
          resolve({ base64: ctx.canvas.toDataURL('image/jpeg'), ...field })
        }
      }
    }
    reader.onerror = reject
  })
}

const getImageFields = (resource, params) => {
  let im_fields = []
  if (resource === 'tickets' || resource === 'ext_tickets_self') {
    if (!params.data.ticket_fields) return []
    im_fields = params.data.ticket_fields
      .filter((field) => {
        return field.type === 'Image' || field.type === 'ImageFireForget'
      })
      .map((field) => {
        return { key: field.source, resize: false }
      })
  } else if (resource === 'ext_tickets') {
    im_fields = params.data.state_transition.input_columns
      .filter((field) => {
        return field.type === 'Image' || field.type === 'ImageFireForget'
      })
      .map((field) => {
        return { key: field.source, resize: false }
      })
  } else {
    im_fields = image_fields[resource] ? image_fields[resource] : []
  }
  return im_fields
}

const disaggregate = (im_fields) => {
  const im_fields_files_instances = []
  im_fields.forEach((ff) => {
    let files = ff.record
    if (files === null || files === undefined) {
      files = []
    }
    for (let k = 0; k < files.length; k++) {
      //we do this check here because the array can contain a mix of string paths and files objects.
      if (isFile(files[k]) === true) {
        im_fields_files_instances.push({
          source: ff.source,
          resize: ff.resize,
          record: files[k],
          index: k,
        })
      }
    }
  })

  return im_fields_files_instances
}

export const myDataProvider = {
  ...dataProvider,
  getList: (resource, params) =>
    dataProvider.getList(resource.replace('modal:', ''), params),
  create: (resource, params) => {
    let im_fields = getImageFields(resource, params)

    let im_fields_files = im_fields.map((field) => {
      return {
        source: field.key,
        resize: field.resize,
        record: params.data[field.key],
      }
    })
    let im_fields_files_instances = disaggregate(im_fields_files)

    return Promise.all(im_fields_files_instances.map(convertFileToBase64)).then(
      (base64Pictures) => {
        base64Pictures.forEach((fieldWithBase64) => {
          const idx = fieldWithBase64.index
          params.data[fieldWithBase64.source][idx].base64 =
            fieldWithBase64.base64
        })

        return dataProvider.create(resource, params)
      }
    )
  },
  update: (resource, params) => {
    if (resource === 'ext_tickets') {
      //necessary for notifications for ext_tickets
      params.data.espaceunique = espaceUniquePrestaPrefix(params.data)
    }

    let im_fields = getImageFields(resource, params)
    if (!im_fields) {
      return dataProvider.update(resource, params)
    }
    let im_fields_files = im_fields.map((field) => ({
      source: field.key,
      resize: field.resize,
      record: params.data[field.key],
    }))
    let im_fields_files_instances = disaggregate(im_fields_files)

    return Promise.all(im_fields_files_instances.map(convertFileToBase64)).then(
      (base64Pictures) => {
        base64Pictures.map((fieldWithBase64) => {
          const idx = fieldWithBase64.index
          params.data[fieldWithBase64.source][idx].base64 =
            fieldWithBase64.base64
        })

        return dataProvider.update(resource, params)
      }
    )
  },
}
